import { SBaseDisplay, SDate, ServiceLocator } from '@summit/core';

import { UserExperienceService } from '@app/services/user-experience.service';
import { MoneyFormatter, RateFormatter, SummitFormattedText } from '@summit/models';
import { EboObjectTimeLine } from '../base-classes/ebo-object-time-line';
import { PerDiemMethod } from '../payroll/per-diem/per-diem-method.enum';
import { OtherParameters } from '../shared/other-parameters';
import { ParameterPolicySummaryMapping } from '../shared/parameter-policy.enum';
import { PayRollParameters } from '../shared/parameters/pay-roll-parameters';
import { ReconcileParameters } from '../shared/parameters/reconcile-parameters';
import { ScheduleParameter } from '../shared/parameters/schedule-parameter';
import { DigitalLogParameter } from '../shared/policies/digital-log-parameter';
import { RoleAccess, RoleAccessMapping } from '../shared/policies/role-access.enum';
import { RequestParameter } from '../shared/request-parameter';
import { UserExperience } from '../shared/user-experience';
import { Division } from './division';
import { DivisionStatus } from './division-status.enum';

export class DivisionParameter extends EboObjectTimeLine implements SBaseDisplay {
  divisionID: string;
  division: Division;
  status: DivisionStatus;
  payRollParameters?: PayRollParameters;
  scheduleParameters?: ScheduleParameter;
  digitalLogParameters?: DigitalLogParameter;
  requestParameters?: RequestParameter;
  reconcileParameters?: ReconcileParameters;
  otherParameters?: OtherParameters;

  private tenantPerDiemMethod: PerDiemMethod;

  constructor(jsonData: Partial<DivisionParameter>) {
    super(jsonData);

    //cache

    //console.log(`Building Division Parameter:`, jsonData, jsonData.otherParameters);
    if (jsonData) {
      if (jsonData.division) this.division = new Division(jsonData.division);
      if (jsonData.scheduleParameters) this.scheduleParameters = new ScheduleParameter(jsonData.scheduleParameters);
      if (jsonData.digitalLogParameters)
        this.digitalLogParameters = new DigitalLogParameter(jsonData.digitalLogParameters);
      // if (jsonData.reconcileParameters?.eldHoursMethod === null) {
      //   console.log(
      //     `DivisionParameter/Reconcile Parameters : ${jsonData.reconcileParameters?.eldHoursMethod} => ${jsonData.reconcileParameters?.eldHoursMethod}`
      //   );
      //   console.trace();
      // }
      if (jsonData.reconcileParameters) {
        this.reconcileParameters = new ReconcileParameters(jsonData.reconcileParameters);
      }
      if (jsonData.payRollParameters) this.payRollParameters = new PayRollParameters(jsonData.payRollParameters);
      if (jsonData.otherParameters) this.otherParameters = new OtherParameters(jsonData.otherParameters);
    }
    this.digitalLogParameters ??= new DigitalLogParameter();
    this.payRollParameters ??= new PayRollParameters();
    this.scheduleParameters ??= new ScheduleParameter();
    this.reconcileParameters ??= new ReconcileParameters();
    this.otherParameters ??= new OtherParameters();
    this.effectiveDate ??= SDate.getNormalized();
  }

  get enablePerDiem(): boolean {
    return this.payRollParameters?.enablePerDiem;
  }

  get enableRestBreak(): boolean {
    return this.payRollParameters?.enableRestBreak;
  }

  get digitalLogParametersSummary(): SummitFormattedText {
    const response = new SummitFormattedText();
    if (!this.digitalLogParameters) {
      response.AddLine('[Corp Default]');
      return response;
    }
    response.AddKeyValues(
      'Linehaul',
      this.digitalLogParameters.linehaulAccess || this.digitalLogParameters.linehaulAccess === RoleAccess.None
        ? RoleAccessMapping[this.digitalLogParameters.linehaulAccess]
        : '[Corp Default]'
    );
    response.AddKeyValues(
      `Spots`,
      this.digitalLogParameters.spotsAccess || this.digitalLogParameters.spotsAccess === RoleAccess.None
        ? RoleAccessMapping[this.digitalLogParameters.spotsAccess]
        : '[Corp Default]'
    );
    response.AddKeyValues(
      'Pre/Post Trip',
      this.digitalLogParameters.prePostTripAccess || this.digitalLogParameters.prePostTripAccess === RoleAccess.None
        ? RoleAccessMapping[this.digitalLogParameters.prePostTripAccess]
        : '[Corp Default]'
    );
    response.AddKeyValues(
      `Hourly`,
      this.digitalLogParameters.hourlyAccess || this.digitalLogParameters.hourlyAccess === RoleAccess.None
        ? RoleAccessMapping[this.digitalLogParameters.hourlyAccess]
        : '[Corp Default]'
    );
    response.AddKeyValues(
      `Expense`,
      this.digitalLogParameters.expenseAccess || this.digitalLogParameters.expenseAccess === RoleAccess.None
        ? RoleAccessMapping[this.digitalLogParameters.expenseAccess]
        : '[Corp Default]'
    );
    response.AddKeyValues(
      `Misc Work`,
      this.digitalLogParameters.miscWorkAccess || this.digitalLogParameters.miscWorkAccess === RoleAccess.None
        ? RoleAccessMapping[this.digitalLogParameters.miscWorkAccess]
        : '[Corp Default]'
    );
    if (this.digitalLogParameters.soloTeamPay == true) {
      response.AddKeyValues(`Solo (Team Pay)`, `Enabled`);
    }
    if (
      this.digitalLogParameters.linehaulAccess !== RoleAccess.None ||
      this.digitalLogParameters.spotsAccess !== RoleAccess.None
    ) {
      if (this.digitalLogParameters.odometerBeginPolicy || this.digitalLogParameters.odometerEndPolicy) {
        response.AddKeyValues(
          `Odometer Begin`,
          this.digitalLogParameters.odometerBeginPolicy
            ? ParameterPolicySummaryMapping[this.digitalLogParameters.odometerBeginPolicy]
            : '[Corp Default]'
        );
        response.AddKeyValues(
          `Odometer End`,
          this.digitalLogParameters.odometerEndPolicy
            ? ParameterPolicySummaryMapping[this.digitalLogParameters.odometerEndPolicy]
            : '[Corp Default]'
        );
      }
      if (this.digitalLogParameters.dollyNumbersPolicy) {
        response.AddKeyValues(
          `Dolly #'s`,
          this.digitalLogParameters.dollyNumbersPolicy
            ? ParameterPolicySummaryMapping[this.digitalLogParameters.dollyNumbersPolicy]
            : '[Corp Default]'
        );
      }
      if (this.digitalLogParameters.sealNumbersPolicy) {
        response.AddKeyValues(
          `Seal #'s`,
          this.digitalLogParameters.sealNumbersPolicy
            ? ParameterPolicySummaryMapping[this.digitalLogParameters.sealNumbersPolicy]
            : '[Corp Default]'
        );
      }
      if (this.digitalLogParameters.trailerNumbersPolicy) {
        response.AddKeyValues(
          `Trailer #'s`,
          this.digitalLogParameters.trailerNumbersPolicy
            ? ParameterPolicySummaryMapping[this.digitalLogParameters.trailerNumbersPolicy]
            : '[Corp Default]'
        );
      }
    }
    if (this.digitalLogParameters.routePolicy) {
      response.AddKeyValues(
        `Route`,
        `${
          this.digitalLogParameters.routePolicy
            ? ParameterPolicySummaryMapping[this.digitalLogParameters.routePolicy]
            : '[Corp Default]'
        }`
      );
    }
    if (this.digitalLogParameters.commentPolicy) {
      response.AddKeyValues(
        `Comments`,
        this.digitalLogParameters.commentPolicy
          ? ParameterPolicySummaryMapping[this.digitalLogParameters.commentPolicy]
          : '[Corp Default]'
      );
    }

    if (response.lines.length == 0) {
      response.AddLine('[Corp Default]');
    }
    return response;
  }

  private _payRollParametersSummary: SummitFormattedText;
  get payRollParametersSummary(): SummitFormattedText {
    if (this._payRollParametersSummary === undefined) {
      this._payRollParametersSummary = new SummitFormattedText();
      if (this.payRollParameters.enablePerDiem) {
        this.updatePerDiemMethod();
        if (this.tenantPerDiemMethod !== PerDiemMethod.RouteBasedMiles) {
          this._payRollParametersSummary.AddKeyValues(
            `Per Diem Rate`,
            MoneyFormatter(this.payRollParameters.perDiemDailyRate)
          );
        } else {
          this._payRollParametersSummary.AddKeyValues(
            `Per Diem Rate Solo`,
            RateFormatter(this.payRollParameters.perDiemSoloRate)
          );
          this._payRollParametersSummary.AddKeyValues(
            `Per Diem Rate Team`,
            RateFormatter(this.payRollParameters.perDiemTeamRate)
          );
        }
      }
      if (this.enableRestBreak == true) {
        this._payRollParametersSummary.AddKeyValues('Rest Breaks', 'Enabled');
      }
      if (this._payRollParametersSummary.lines.length == 0) {
        this._payRollParametersSummary.AddLine('[Corp Default]');
      }
    }
    return this._payRollParametersSummary;
  }

  private _reconcileParametersSummary: SummitFormattedText;
  getReconcileParametersSummary(corporate: boolean): SummitFormattedText {
    if (this._reconcileParametersSummary === undefined) {
      this._reconcileParametersSummary = this.reconcileParameters?.toFormattedText(corporate);
    }
    return this._reconcileParametersSummary;
  }
  get reconcileParametersSummary(): SummitFormattedText {
    return this.getReconcileParametersSummary(false);
  }

  get scheduleParametersString(): SummitFormattedText {
    return this.scheduleParameters.getSummary();
  }

  private _otherParametersSummary: SummitFormattedText;
  get otherParametersSummary(): SummitFormattedText {
    if (this._otherParametersSummary === undefined) {
      this._otherParametersSummary = new SummitFormattedText();
      //console.log('Other Parameters:', this.otherParameters);
      if (this.otherParameters?.financialAccess || this.otherParameters?.financialAccess === RoleAccess.None)
        this._otherParametersSummary.AddKeyValues(
          'Financial Access',
          RoleAccessMapping[this.otherParameters.financialAccess]
        );
      else this._otherParametersSummary.AddKeyValues('Financial Access', '[Corp Default]');
      if (this.otherParameters?.disputeAccess || this.otherParameters?.disputeAccess === RoleAccess.None)
        this._otherParametersSummary.AddKeyValues(
          'Dispute Access',
          RoleAccessMapping[this.otherParameters.disputeAccess]
        );
      if (this.otherParameters?.hasLocalShop) this._otherParametersSummary.AddKeyValues('Has Local Shop', 'Yes');
      else if (this.otherParameters?.hasLocalShop === false)
        this._otherParametersSummary.AddKeyValues('Has Local Shop', 'No');
      else this._otherParametersSummary.AddKeyValues('Has Local Shop', '[Corp Default]');
    }
    return this._otherParametersSummary;
  }

  toShortString(): string {
    return `${this.effectiveDate.format('M/D/YY')} - Parameters`;
  }

  /*noUnusedParameters private getTextString(inputBoolean?: boolean) {
    switch (inputBoolean) {
      case true:
        return 'Yes';
      case false:
        return 'No';
      default:
        return '[Corp Default]';
    }
  }
 */
  isEquivalent(other: DivisionParameter): boolean {
    if (!this.effectiveDate.isSame(other?.effectiveDate)) return false;
    if (this.status != other?.status) return false;
    if (!this.payRollParameters.isEquivalent(other?.payRollParameters)) return false;
    if (!this.digitalLogParameters.isEquivalent(other?.digitalLogParameters)) return false;
    if (!this.reconcileParameters.isEquivalent(other?.reconcileParameters)) return false;
    if (!this.scheduleParameters.isEquivalent(other?.scheduleParameters)) return false;
    if (!this.otherParameters.isEquivalent(other?.otherParameters)) return false;
    // if (!this.requestParameters.isEquivalent(other?.requestParameters)) return false; TODO: 2347  Add Request Parameters
    return true;
  }

  override isHistorical(): boolean {
    return this.effectiveDate.isBefore(SDate.new().add(7, 'day'));
  }

  clearCache() {
    this._reconcileParametersSummary = undefined;
    this._otherParametersSummary = undefined;
  }

  private updatePerDiemMethod() {
    const uxService = ServiceLocator.injector?.get(UserExperienceService);
    this.tenantPerDiemMethod = (
      uxService.getUserExperience() as UserExperience
    ).activeEmployee.tenant.payroll.perDiemMethod;
  }
}
